import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEO from "@/components/feature/SEO";
import { servicesOverview } from "@/mocks/services";

export default function ServicesOverviewPage() {
  const { t } = useTranslation("home");

  return (
    <Layout>
      <SEO
        title="Nuestros Servicios HVAC, Electricos y UPS | Ventra Core Leon"
        description="Servicios integrales de climatizacion HVAC, congelacion, sistemas electricos especializados, energia UPS, instalacion y mantenimiento en Leon, Guanajuato. Mas de 15 anos de experiencia."
        keywords="servicios HVAC, climatizacion, congelacion, refrigeracion, sistemas electricos, energia UPS, mantenimiento, Leon Guanajuato"
        canonical="https://ventracore.com.mx/servicios"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://ventracore.com.mx/servicios#webpage",
            "url": "https://ventracore.com.mx/servicios",
            "name": "Nuestros Servicios HVAC, Electricos y UPS | Ventra Core",
            "isPartOf": { "@id": "https://ventracore.com.mx/#website" },
            "description": "Servicios integrales de climatizacion HVAC, congelacion, sistemas electricos y energia UPS en Leon, Guanajuato.",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Inicio",
                "item": "https://ventracore.com.mx/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Servicios",
                "item": "https://ventracore.com.mx/servicios",
              },
            ],
          },
        ]}
      />
      <div className="w-full">
        {/* Hero */}
        <section className="relative w-full bg-brandgreen py-12 md:py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundRepeat: "repeat",
              }}
            />
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 uppercase tracking-wide">
              {t("servicesPage.title")}
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-3xl mx-auto">
              {servicesOverview.description}
            </p>
            <div className="w-20 h-1 bg-white/50 mx-auto mt-6" />
          </div>
        </section>

        {/* Services Grid */}
        <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {servicesOverview.services.map((svc) => (
              <Link
                key={svc.slug}
                to={`/${svc.slug}`}
                className="group flex flex-col bg-surface rounded-lg overflow-hidden border border-transparent hover:border-brandgreen/30 transition-all duration-300 cursor-pointer"
              >
                <div className="w-full h-48 md:h-56 overflow-hidden">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold text-magenta mb-2 group-hover:text-brandgreen transition-colors">
                    {svc.title}
                  </h3>
                  <p className="text-sm text-text-muted leading-relaxed flex-1">
                    {svc.shortDesc}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-brandgreen">
                    <span>{t("servicesPage.learnMore")}</span>
                    <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-magenta py-10 md:py-14">
          <div className="w-full px-4 md:px-6 text-center">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 uppercase tracking-wide">
              {t("coolnetPage.cta")}
            </h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+524772847060"
                className="inline-flex items-center gap-2 bg-white text-magenta font-semibold px-6 py-3 rounded-md hover:bg-surface transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </span>
                +52 477 284 70 60
              </a>
              <a
                href="mailto:comercial@ventracore.com.mx"
                className="inline-flex items-center gap-2 bg-white text-magenta font-semibold px-6 py-3 rounded-md hover:bg-surface transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                comercial@ventracore.com.mx
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}